export const ACTIVITY_STATUSES = [
  { id: 'todo', title: 'Pendientes', tone: 'info' },
  { id: 'in_progress', title: 'En curso', tone: 'primary' },
  { id: 'blocked', title: 'En espera / bloqueadas', tone: 'error' },
  { id: 'done', title: 'Terminadas', tone: 'success' }
];

export const normalizeActivityStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  return ({ pending: 'todo', completed: 'done', holding: 'blocked' })[status]
    || (ACTIVITY_STATUSES.some((item) => item.id === status) ? status : 'todo');
};

export const activityFilterLabel = (value) => ({
  '': 'Todas las actividades', active: 'Actividades abiertas', overdue: 'Actividades vencidas', due_soon: 'Por vencer (hoy y próximos 2 días)'
})[value] || ACTIVITY_STATUSES.find((item) => item.id === value)?.title || 'Todas las actividades';

export const parseActivityDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value);
  const key = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

export const activityDueState = (task, now = new Date()) => {
  if (normalizeActivityStatus(task.status) === 'done') return 'completed';
  const due = parseActivityDate(task.due_date);
  if (!due) return 'undated';
  const today = new Date(now);
  const dayNumber = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
  const remaining = dayNumber(due) - dayNumber(today);
  return remaining < 0 ? 'overdue' : remaining <= 2 ? 'due_soon' : 'later';
};

export const matchesActivityFilter = (task, value, now) => {
  const status = normalizeActivityStatus(task.status);
  if (value === 'active') return status !== 'done';
  if (value === 'overdue' || value === 'due_soon') return activityDueState(task, now) === value;
  return !value || status === value;
};

const searchable = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const filterActivityScope = (tasks, filters = {}) => tasks.filter((task) => {
  const query = searchable(filters.q).trim();
  const haystack = [task.id, task.title, task.description, task.project_name, task.assigned_to_name, task.last_comment].map(searchable).join(' ');
  const projectMatches = filters.project === 'internal' ? !task.project_id : !filters.project || String(task.project_id) === String(filters.project);
  return (!query || haystack.includes(query))
    && (!filters.assignee || (task.assigned_to_name || 'Sin asignar') === filters.assignee)
    && (!filters.priority || task.priority === filters.priority)
    && projectMatches;
});

export const summarizeActivities = (tasks, now = new Date()) => {
  const result = { total: tasks.length, todo: 0, inProgress: 0, blocked: 0, done: 0, active: 0, overdue: 0, dueSoon: 0 };
  tasks.forEach((task) => {
    const status = normalizeActivityStatus(task.status);
    result[status === 'in_progress' ? 'inProgress' : status] += 1;
    if (status !== 'done') result.active += 1;
    const due = activityDueState(task, now);
    if (due === 'overdue') result.overdue += 1;
    if (due === 'due_soon') result.dueSoon += 1;
  });
  return result;
};

// Personal daily queue: each open task belongs to exactly one group.
export const buildMyDay = (tasks, userId, now = new Date()) => {
  const groups = { overdue: [], today: [], priority: [], other: [] };
  const today = parseActivityDate(now);
  if (!userId || !today) return groups;
  tasks.forEach((task) => {
    if (String(task.assigned_to) !== String(userId) || normalizeActivityStatus(task.status) === 'done') return;
    const due = parseActivityDate(task.due_date);
    const group = activityDueState(task, now) === 'overdue' ? 'overdue'
      : due && due.toDateString() === today.toDateString() ? 'today'
        : ['high', 'critical'].includes(task.priority) ? 'priority' : 'other';
    groups[group].push(task);
  });
  const rank = { critical: 0, high: 1, medium: 2, low: 3 };
  Object.values(groups).forEach((items) => items.sort((a, b) => (
    (rank[a.priority] ?? 4) - (rank[b.priority] ?? 4)
    || (parseActivityDate(a.due_date)?.getTime() ?? Infinity) - (parseActivityDate(b.due_date)?.getTime() ?? Infinity)
    || String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
  )));
  return groups;
};
